import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import { API_URL, apiRequest } from '../config/api';

// Simple in-memory cache scoped to module lifetime
const membersCacheByProjectId = {};

const ProjectMembers = () => {
  const { t } = useTranslation();
  const { user, handleAuthError } = useAuth();
  const { selectedProject } = useProjects();
  
  // Get projectId from selectedProject context
  const projectId = selectedProject?.id;
  
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({
    email: '',
    role: ''
  });

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const hasHydratedFromCacheRef = useRef(false);

  // Helper function to translate role names
  const translateRole = (role) => {
    const roleMap = {
      'OWNER': t('projectMembers.modal.roles.owner'),
      'EDITOR': t('projectMembers.modal.roles.editor'),
      'VIEWER': t('projectMembers.modal.roles.viewer')
    };
    return roleMap[role] || role;
  };

  // Function to fetch project members from backend
  const fetchMembers = useCallback(async () => {
    if (!projectId) return;
    const hasCached = Array.isArray(membersCacheByProjectId[projectId]);
    try {
      if (!hasCached) {
        setLoading(true);
      }
      const response = await apiRequest(`${API_URL}/api/projects/${projectId}/access`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      }, handleAuthError);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const membersData = await response.json();
      
      // Transform backend response to match our local state structure
      const transformedMembers = membersData.map(member => ({
        id: member.userId,
        name: member.email.split('@')[0], // Use email prefix as name
        email: member.email,
        role: member.role
      }));
      
      membersCacheByProjectId[projectId] = transformedMembers;
      setMembers(transformedMembers);
      console.log('Fetched members:', transformedMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
      // Don't show alert here as it might be annoying on page load
    } finally {
      if (!hasCached) {
        setLoading(false);
      }
    }
  }, [projectId, user.token, handleAuthError]);

  // Hydrate from cache and refresh in background
  useEffect(() => {
    if (projectId && Array.isArray(membersCacheByProjectId[projectId])) {
      setMembers(membersCacheByProjectId[projectId]);
      hasHydratedFromCacheRef.current = true;
    } else {
      setMembers([]);
      hasHydratedFromCacheRef.current = false;
    }
    fetchMembers();
  }, [projectId, fetchMembers]);

  const handleCheckboxChange = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === members.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(members.map(item => item.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    
    const message = selectedRows.length === 1 
      ? t('projectMembers.confirmations.deleteSingle')
      : t('projectMembers.confirmations.deleteMultiple', { count: selectedRows.length });
    
    if (window.confirm(message)) {
      try {
        // Get selected members
        const selectedMembers = members.filter(member => selectedRows.includes(member.id));
        
        // Delete each selected member
        for (const member of selectedMembers) {
          const response = await fetch(`${API_URL}/api/projects/${projectId}/access`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${user.token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: member.email
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          console.log('Backend response for delete: Success (status:', response.status, ')');
        }
        
        // Refresh members list from backend to get updated data
        await fetchMembers();
        console.log('Deleted members:', selectedMembers.map(m => m.email));
        
        // Clear selected rows
        setSelectedRows([]);
      } catch (error) {
        console.error('Error deleting members:', error);
        alert(t('projectMembers.confirmations.deleteMultipleFailed'));
      }
    }
  };

  const handleAddMember = () => {
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMember(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Mock search results - in real app this would come from API
  // const mockSearchResults = [
  //   { id: 101, name: 'John Doe', email: 'john.doe@company.com' },
  //   { id: 102, name: 'Jane Smith', email: 'jane.smith@company.com' },
  //   { id: 103, name: 'Mike Johnson', email: 'mike.johnson@company.com' },
  //   { id: 104, name: 'Sarah Wilson', email: 'sarah.wilson@company.com' },
  //   { id: 105, name: 'David Brown', email: 'david.brown@company.com' }
  // ];

  // const filteredUsers = mockSearchResults.filter(user =>
  //   user.name.toLowerCase().includes(newMember.searchQuery.toLowerCase()) ||
  //   user.email.toLowerCase().includes(newMember.email.toLowerCase())
  // );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectId) {
      alert(t('projectMembers.confirmations.selectProjectFirst'));
      return;
    }
    
    if (!newMember.email || !newMember.role) {
      alert(t('projectMembers.confirmations.enterEmailAndRole'));
      return;
    }
    
    if (editingMember) {
      // Update existing member - send request to backend
      try {
        const response = await fetch(`${API_URL}/api/projects/${projectId}/access`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: newMember.email,
            role: newMember.role
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

                 const result = await response.json();
         console.log('Backend response for update:', result);

         // Refresh members list from backend to get updated data
         await fetchMembers();
         console.log('Updated member:', { ...editingMember, role: newMember.role });
      } catch (error) {
        console.error('Error updating member:', error);
        alert(t('projectMembers.confirmations.updateFailed'));
        return;
      }
    } else {
      // Add new member - send request to backend
      try {  
         const response = await fetch(`${API_URL}/api/projects/${projectId}/access`, {
           method: 'POST',
           headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
           },
                       body: JSON.stringify({
              email: newMember.email,
              role: newMember.role
            })
         });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

                 const result = await response.json();
         console.log('Backend response:', result);

         // Refresh members list from backend to get the newly added member
         await fetchMembers();
         console.log('Added new member with email:', newMember.email);
      } catch (error) {
        console.error('Error adding member:', error);
        alert(t('projectMembers.confirmations.addFailed'));
        return;
      }
    }
    
    // Reset form and close modal
    setNewMember({
      email: '',
      role: ''
    });
    setEditingMember(null);
    setShowModal(false);
  };

  const handleCancel = () => {
    setNewMember({
      email: '',
      role: ''
    });
    setEditingMember(null);
    setShowModal(false);
  };

  // const handleUserSelect = (user) => {
  //   setNewMember(prev => ({
  //     ...prev,
  //     selectedUser: user,
  //     searchQuery: `${user.name} (${user.email})`
  //   }));
  // };

  const handleEdit = (member) => {
    setEditingMember(member);
    setNewMember({
      email: member.email,
      role: member.role
    });
    setShowModal(true);
  };

  const handleDelete = async (memberId) => {
    const memberToDelete = members.find(member => member.id === memberId);
    if (!memberToDelete) return;
    
    if (window.confirm(t('projectMembers.confirmations.deleteSingle'))) {
      try {
        const response = await fetch(`${API_URL}/api/projects/${projectId}/access`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: memberToDelete.email
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        console.log('Backend response for delete: Success (status:', response.status, ')');
        
        // Refresh members list from backend to get updated data
        await fetchMembers();
        console.log('Deleted member:', memberToDelete.email);
      } catch (error) {
        console.error('Error deleting member:', error);
        alert(t('projectMembers.confirmations.deleteFailed'));
      }
    }
  };

  // Show message if no project is selected
  if (!projectId) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-12">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('projectMembers.noProjectSelected.title')}</h3>
          <p className="text-gray-500 mb-6">{t('projectMembers.noProjectSelected.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div>
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('projectMembers.title')}</h1>
          
          <div className="flex items-start justify-between">
            <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
              {t('projectMembers.description')}
            </p>
            <button 
              onClick={handleAddMember}
              className="ml-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex-shrink-0"
            >
              {t('projectMembers.addNewMember')}
            </button>
          </div>
        </div>

        {/* Table Section */}
        {!loading && members.length === 0 ? (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-12">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('projectMembers.noMembers.title')}</h3>
              <p className="text-gray-500 mb-6">{t('projectMembers.noMembers.description')}</p>
              <button
                onClick={handleAddMember}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                {t('projectMembers.noMembers.addButton')}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
             {/* Bulk Actions */}
             {selectedRows.length > 0 && (
               <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                 <div className="text-sm text-gray-700">
                   {t('projectMembers.bulkActions.selected', { count: selectedRows.length })}
                 </div>
                 <button
                   onClick={handleDeleteSelected}
                   className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-md transition-colors duration-200 flex items-center space-x-1"
                 >
                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                   </svg>
                   <span>{t('projectMembers.bulkActions.deleteSelected')}</span>
                 </button>
               </div>
             )}
             
             <div className="overflow-x-auto">
               <table className="w-full">
                 <thead className="bg-gray-100 border-b border-gray-200">
                   <tr>
                     <th className="px-6 py-3 text-left">
                       <input
                         type="checkbox"
                         checked={selectedRows.length === members.length}
                         onChange={handleSelectAll}
                         className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                       />
                     </th>
                     <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                       {t('projectMembers.table.name')}
                     </th>
                     <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                       {t('projectMembers.table.role')}
                     </th>
                     <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 tracking-wider">
                       
                     </th>
                   </tr>
                 </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <>
                      {Array.from({ length: 8 }).map((_, idx) => (
                        <tr key={idx} className="animate-pulse">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="w-4 h-4 bg-gray-200 rounded-sm" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="h-4 bg-gray-200 rounded w-40 mb-2" />
                              <div className="h-3 bg-gray-200 rounded w-56" />
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="h-4 bg-gray-200 rounded w-24" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex space-x-2 justify-end">
                              <div className="w-4 h-4 bg-gray-200 rounded" />
                              <div className="w-4 h-4 bg-gray-200 rounded" />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </>
                  ) : members.map((member) => (
                     <tr 
                       key={member.id} 
                       className="hover:bg-gray-50 transition-colors duration-150"
                     >
                       <td className="px-6 py-4 whitespace-nowrap">
                         <input
                           type="checkbox"
                           checked={selectedRows.includes(member.id)}
                           onChange={() => handleCheckboxChange(member.id)}
                           className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                         />
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div>
                           <div className="text-sm font-medium text-gray-900">
                             {member.name}
                           </div>
                           <div className="text-sm text-gray-500">
                             {member.email}
                           </div>
                         </div>
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                         {translateRole(member.role)}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                         <div className="flex space-x-2 justify-end">
                           <button
                             onClick={() => handleEdit(member)}
                             className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                             title={t('common.edit')}
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                             </svg>
                           </button>
                           <button
                             onClick={() => handleDelete(member.id)}
                             className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                             title={t('common.delete')}
                           >
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                             </svg>
                           </button>
                         </div>
                       </td>
                     </tr>
                  ))}
                 </tbody>
               </table>
             </div>

             {/* Pagination Controls */}
            {!loading && (
              <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="text-sm text-gray-700">
                    {t('projectMembers.pagination.showing', { total: members.length })}
                  </div>
                </div>
              </div>
            )}
           </div>
         )}
       </div>

       {/* Modal */}
       {showModal && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
             <div className="mt-3">
                               <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingMember ? t('projectMembers.modal.editTitle') : t('projectMembers.modal.addTitle')}
                </h3>
                               <form onSubmit={handleSubmit}>
                  {!editingMember && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectMembers.modal.email')}</label>
                        <input
                          type="email"
                          name="email"
                          value={newMember.email || ''}
                          onChange={handleInputChange}
                          placeholder={t('projectMembers.modal.emailPlaceholder')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Edit Mode - Show current member info */}
                  {editingMember && (
                    <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <div className="text-sm font-medium text-gray-900">{t('projectMembers.modal.editingMember')}</div>
                      <div className="text-sm text-gray-700">{editingMember.name}</div>
                      <div className="text-xs text-gray-600">{editingMember.email}</div>
                    </div>
                  )}
                 <div className="mb-6">
                   <label className="block text-sm font-medium text-gray-700 mb-1">{t('projectMembers.modal.role')}</label>
                                       <select
                      name="role"
                      value={newMember.role}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">{t('projectMembers.modal.rolePlaceholder')}</option>
                      <option value="OWNER">{t('projectMembers.modal.roles.owner')}</option>
                      <option value="EDITOR">{t('projectMembers.modal.roles.editor')}</option>
                      <option value="VIEWER">{t('projectMembers.modal.roles.viewer')}</option>
                    </select>
                 </div>
                 <div className="flex justify-end space-x-3">
                   <button
                     type="button"
                     onClick={handleCancel}
                     className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors duration-200"
                   >
                     {t('common.cancel')}
                   </button>
                                       <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200"
                    >
                      {editingMember ? t('projectMembers.modal.updateMember') : t('projectMembers.modal.addMember')}
                    </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default ProjectMembers; 